import React, { useState } from "react";
import Select from "react-select";
import { CreditCard, ChevronUp, ChevronDown, Trash2 } from "react-feather";

// ── Note ──────────────────────────────────────────────────────────────────────
// framer-motion removed — the original used <motion.div> but passed animate/
// initial as regular HTML props on a plain div, which React ignores silently.
// Replaced with CSS transition (max-height + opacity + transform) which gives
// the same smooth expand/collapse without the extra dependency.
// ─────────────────────────────────────────────────────────────────────────────

const BillSummary = ({
  subtotal,
  taxTotal,
  taxBreakup,
  deliveryCharge,
  serviceCharge,
  grandTotal,
  unroundedTotal,
  discountPercentage,
  setDiscountPercentage,
  taxIncluded,
  setTaxIncluded,
  isComplementary,
  setIsComplementary,
  isPartialPaymentEnabled,
  setIsPartialPaymentEnabled,
  partialPayments,
  setPartialPayments,
  currentPartialMethod,
  setCurrentPartialMethod,
  currentPartialAmount,
  setCurrentPartialAmount,
  handleAddPartialPayment,
  handleRemovePartialPayment,
  paymentMode,
  setPaymentMode,
  paymentOptions,
  isFooterExpanded,
  setIsFooterExpanded,
  handleGenerateBill,
  billItems,
}) => {
  const safeUnroundedTotal = unroundedTotal !== undefined ? unroundedTotal : 0;
  const safeGrandTotal     = grandTotal     !== undefined ? grandTotal     : 0;
  const discountedTotal    = safeGrandTotal * (1 - (discountPercentage || 0) / 100);

  return (
    <div className="pos-bill-summary">

      {/* ── Inline styles for smooth expand/collapse ── */}
      <style>{`
        .pbs-footer-body {
          overflow: hidden;
          transition:
            max-height 0.36s cubic-bezier(0.4, 0, 0.2, 1),
            opacity    0.26s ease,
            transform  0.30s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: max-height, opacity, transform;
          transform-origin: bottom;
        }
        .pbs-footer-body.is-open {
          max-height: 700px;
          opacity: 1;
          transform: translateY(0) scaleY(1);
        }
        .pbs-footer-body.is-closed {
          max-height: 0;
          opacity: 0;
          transform: translateY(6px) scaleY(0.97);
        }
        .pbs-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          color: #212529;
          padding: 4px 0;
          border-bottom: 1px dashed #e9ecef;
        }
        .pbs-toggle-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .pbs-toggle {
          display: inline-block;
          width: 40px;
          height: 20px;
          border-radius: 20px;
          position: relative;
          cursor: pointer;
          transition: background 0.22s ease;
          flex: 0 0 auto;
        }
        .pbs-toggle-thumb {
          position: absolute;
          top: 2px;
          width: 16px;
          height: 16px;
          background: white;
          border-radius: 50%;
          transition: left 0.22s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }
        .pbs-circle-btn {
          background: #007bff;
          color: white;
          border: none;
          border-radius: 50%;
          width: 36px;
          height: 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          transition: background 0.18s ease, transform 0.14s ease;
          flex: 0 0 auto;
        }
        .pbs-circle-btn:hover { background: #0056b3; transform: translateY(-1px); }
      `}</style>

      {/* ── Tax + Complementary toggles ── */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, gap: 8 }}>
        {[
          { id: "pbs-tax", checked: taxIncluded, onChange: (e) => setTaxIncluded(e.target.checked), label: "Include Tax" },
          { id: "pbs-comp", checked: isComplementary, onChange: (e) => setIsComplementary(e.target.checked), label: "Complementary" },
        ].map(({ id, checked, onChange, label }) => (
          <label key={id} className="pbs-toggle-wrap" htmlFor={id} style={{ cursor: "pointer" }}>
            <input id={id} type="checkbox" checked={checked} onChange={onChange} style={{ display: "none" }} />
            <span
              className="pbs-toggle"
              style={{ background: checked ? "#28a745" : "#ced4da" }}
            >
              <span className="pbs-toggle-thumb" style={{ left: checked ? 22 : 2 }} />
            </span>
            <span style={{ fontSize: 12 }}>{label}</span>
          </label>
        ))}
      </div>

      {/* ── Payment method select ── */}
      <Select
        options={paymentOptions}
        value={paymentMode}
        onChange={setPaymentMode}
        placeholder="Select Payment Method"
        styles={{
          control: (base) => ({
            ...base,
            borderRadius: 8,
            borderColor: "#ced4da",
            fontSize: 12,
            height: 38,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }),
          menu: (base) => ({ ...base, zIndex: 1001 }),
        }}
      />

      {/* ── Footer strip: total + expand toggle ── */}
      <div style={{ marginTop: 15, borderRadius: 8, border: "1px solid #dee2e6", overflow: "hidden" }}>

        {/* Always-visible total bar */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 12px",
            background: "#fff",
            borderBottom: isFooterExpanded ? "1px solid #dee2e6" : "none",
          }}
        >
          <span style={{ fontSize: 20, fontWeight: 700, color: "#28a745" }}>
            Total ₹{discountedTotal.toFixed(2)}
          </span>
          <button
            className="pbs-circle-btn"
            onClick={() => setIsFooterExpanded((p) => !p)}
            aria-label={isFooterExpanded ? "Collapse detail" : "Expand detail"}
            aria-expanded={isFooterExpanded}
          >
            {isFooterExpanded ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
          </button>
        </div>

        {/* Expandable detail body */}
        <div className={`pbs-footer-body ${isFooterExpanded ? "is-open" : "is-closed"}`}>
          <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 }}>

            <div className="pbs-row">
              <span>Subtotal:</span>
              <span>₹{(subtotal || 0).toFixed(2)}</span>
            </div>

            {(taxBreakup || []).map((tax, index) => (
              <div key={index} className="pbs-row">
                <span>{tax.tax_name} ({tax.tax_rate}%):</span>
                <span>₹{Number(tax.amount || 0).toFixed(2)}</span>
              </div>
            ))}

            {(taxBreakup || []).length > 0 && (
              <div className="pbs-row" style={{ fontWeight: 600 }}>
                <span>Total Tax:</span>
                <span>₹{(taxTotal || 0).toFixed(2)}</span>
              </div>
            )}

            {(serviceCharge || 0) > 0 && (
              <div className="pbs-row">
                <span>Service Charge:</span>
                <span>₹{serviceCharge.toFixed(2)}</span>
              </div>
            )}

            {(deliveryCharge || 0) > 0 && (
              <div className="pbs-row">
                <span>Delivery Charge:</span>
                <span>₹{deliveryCharge.toFixed(2)}</span>
              </div>
            )}

            {/* Discount input */}
            <div className="pbs-row">
              <span>Discount (%):</span>
              <input
                type="number"
                min="0"
                max="100"
                value={discountPercentage}
                onChange={(e) => setDiscountPercentage(Math.min(100, Math.max(0, Number(e.target.value))))}
                style={{
                  width: 60, padding: "4px 6px", borderRadius: 6,
                  border: "1px solid #ced4da", fontSize: 12, textAlign: "right",
                }}
              />
            </div>

            <div className="pbs-row">
              <span>Original Total:</span>
              <span>₹{safeUnroundedTotal.toFixed(2)}</span>
            </div>

            <div className="pbs-row" style={{ fontWeight: 700, borderBottom: "none" }}>
              <span>Grand Total (Rounded):</span>
              <span>₹{discountedTotal.toFixed(2)}</span>
            </div>

            <small style={{ color: "#6c757d", fontSize: 10, textAlign: "right" }}>
              *Rounded to nearest rupee as per settings
            </small>

            {/* Partial payment toggle */}
            <label className="pbs-toggle-wrap" htmlFor="pbs-partial" style={{ marginTop: 8, cursor: "pointer" }}>
              <input
                id="pbs-partial"
                type="checkbox"
                checked={isPartialPaymentEnabled}
                onChange={() => setIsPartialPaymentEnabled(!isPartialPaymentEnabled)}
                style={{ display: "none" }}
              />
              <span
                className="pbs-toggle"
                style={{ background: isPartialPaymentEnabled ? "#28a745" : "#ced4da" }}
              >
                <span className="pbs-toggle-thumb" style={{ left: isPartialPaymentEnabled ? 22 : 2 }} />
              </span>
              <span style={{ fontSize: 12 }}>Partial Payment</span>
            </label>

            {isPartialPaymentEnabled && (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8, animation: "nsBillIn 0.2s ease both" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Select
                    options={(paymentOptions || []).filter((p) => p.value !== "Due")}
                    value={(paymentOptions || []).find((p) => p.value === currentPartialMethod)}
                    onChange={(option) => setCurrentPartialMethod(option?.value || "Cash")}
                    styles={{
                      control: (base) => ({ ...base, width: 120, minHeight: 34, fontSize: 12 }),
                      menu:    (base) => ({ ...base, zIndex: 1001 }),
                    }}
                  />
                  <input
                    type="number"
                    value={currentPartialAmount}
                    onChange={(e) => setCurrentPartialAmount(e.target.value)}
                    placeholder="Amount"
                    step="0.01"
                    style={{
                      padding: "5px 8px", borderRadius: 6,
                      border: "1px solid #ced4da", width: 100, fontSize: 12,
                    }}
                  />
                  <button
                    onClick={handleAddPartialPayment}
                    style={{
                      padding: "5px 14px", borderRadius: 6,
                      background: "#007bff", color: "white",
                      border: "none", fontSize: 12, cursor: "pointer",
                      transition: "background 0.16s ease",
                    }}
                  >
                    Add
                  </button>
                </div>

                {(partialPayments || []).map((payment, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex", alignItems: "center",
                      justifyContent: "space-between", padding: "6px 0",
                      borderBottom: index < partialPayments.length - 1 ? "1px solid #e9ecef" : "none",
                    }}
                  >
                    <span style={{ fontSize: 12 }}>
                      {payment.method}: ₹{Number(payment.amount || 0).toFixed(2)}
                    </span>
                    <button
                      onClick={() => handleRemovePartialPayment(index)}
                      style={{
                        padding: "4px 8px", background: "#dc3545", color: "white",
                        border: "none", borderRadius: 4,
                        display: "inline-flex", alignItems: "center",
                        cursor: "pointer", transition: "background 0.16s ease",
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}

                {(partialPayments || []).length > 0 && (
                  <div style={{ textAlign: "right", fontSize: 12, color: "#6c757d" }}>
                    Due: ₹{(discountedTotal - (partialPayments || []).reduce((s, p) => s + Number(p.amount || 0), 0)).toFixed(2)}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default BillSummary;